import { Component, EventEmitter, Output, OnDestroy } from '@angular/core'
import { ClusteringNormalization, PipelineType, TsneParameters } from '../../../shared/model/jobs'
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { Subject, take } from 'rxjs'
import { Store } from '@ngrx/store'
import {
    selectCaseMarkers,
    selectCaseSegObjectPropertyMap,
    selectCaseSegObjectPropertyMaps,
} from '../../../state/case/case.selectors'
// import { environment } from 'src/environments/environment'
import { JobApiService } from 'src/app/shared/services/job-api.service'
import { closeJobSelectionDialog } from 'src/app/shared/utils/job-selection-dialog-utils'
import { HttpErrorResponse } from '@angular/common/http'
import { ToasterService } from '../../../shared/services/toaster.service'
import { selectImageLayers } from 'src/app/state/viewer/viewer.selectors'

@Component({
    selector: 'app-auto-cell-detect',
    templateUrl: './auto-cell-detect.component.html',
    styleUrls: ['./auto-cell-detect.component.scss'],
})
export class AutoCellDetectComponent implements OnDestroy {
    ClusteringNormalizationValues = Object.values(ClusteringNormalization)

    autoCellDetectForm: FormGroup

    createJobMode = true
    selectedMarkers: string[] = []
    readonly clusteringMarkers$ = this.store.select(selectCaseMarkers)
    readonly SELECTED_INTENSITY_MAP_KEY = 'selectedIntensityMap'
    readonly NEW_EXPERIMENT_NAME_KEY = 'newExperimentName'
    readonly LAYER_TYPE_MARKERS_KEY = 'markers'
    readonly LAYER_TYPE_TRANSCRIPTS_KEY = 'transcripts'
    readonly LAYER_TYPE_MORPHOMETRICS_KEY = 'morphometrics'

    readonly layerTypes = [
        { name: 'Marker', formControl: this.LAYER_TYPE_MARKERS_KEY },
        { name: 'Transcripts', formControl: this.LAYER_TYPE_TRANSCRIPTS_KEY },
        { name: 'Morphometrics', formControl: this.LAYER_TYPE_MORPHOMETRICS_KEY },
    ]

    private readonly destroy$ = new Subject<void>()
    enableJobButton = false
    // private readonly wikiUrl = environment.wikiUrl
    readonly segObjectPropertyMap$
    readonly segObjectPropertyMaps$
    readonly sourceLayers$ = this.store.select(selectImageLayers)

    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly store: Store,
        private readonly jobApi: JobApiService,
        private readonly toaster: ToasterService
    ) {
        this.segObjectPropertyMap$ = this.store.select(selectCaseSegObjectPropertyMap)
        this.segObjectPropertyMaps$ = this.store.select(selectCaseSegObjectPropertyMaps)

        this.initiateForm()
    }

    ngOnDestroy(): void {
        this.destroy$.next()
        this.destroy$.complete()
    }

    @Output() paramsSet = new EventEmitter<TsneParameters>()

    startJob(): void {
        this.segObjectPropertyMaps$.pipe(take(1)).subscribe(maps => {
            const inputFeature = ['markers', 'morphometrics', 'transcripts'].filter(
                feature => this.autoCellDetectForm.value[feature]
            )
            this.jobApi
                .startClusteringJob({
                    type: PipelineType.AutoCellDetection,
                    ...this.autoCellDetectForm.value,
                    input_feature: inputFeature,
                    markers: this.selectedMarkers,
                    seg_object_property_map: maps.find(
                        m => m.name === this.autoCellDetectForm.value.selectedIntensityMap
                    ),
                })
                .subscribe({
                    next: () => {
                        closeJobSelectionDialog()
                        this.initiateForm()
                    },
                    error: (err: HttpErrorResponse) => {
                        this.toaster.httpError(err, 'Error starting job')
                    },
                })
            this.selectedMarkers = []
        })
    }

    private initiateForm(): void {
        this.segObjectPropertyMaps$.pipe(take(1)).subscribe(maps => {
            this.buildForm({
                name: maps[0]?.name,
                markers: false,
                transcripts: false,
                morphometrics: false,
            })

            this.autoCellDetectForm.valueChanges.subscribe(formValue => {
                const hasIntensityMap = !!formValue[this.SELECTED_INTENSITY_MAP_KEY]
                const hasExperimentName = !!formValue[this.NEW_EXPERIMENT_NAME_KEY]
                const selectedLayerCount = [
                    this.LAYER_TYPE_MARKERS_KEY,
                    this.LAYER_TYPE_TRANSCRIPTS_KEY,
                    this.LAYER_TYPE_MORPHOMETRICS_KEY,
                ].filter(key => formValue[key]).length

                this.enableJobButton =
                    hasIntensityMap && hasExperimentName && selectedLayerCount >= 1
            })
        })
    }

    buildForm(experiment: any) {
        this.autoCellDetectForm = this.formBuilder.group({
            [this.SELECTED_INTENSITY_MAP_KEY]: new FormControl(experiment?.name, [
                Validators.required,
            ]),
            [this.NEW_EXPERIMENT_NAME_KEY]: new FormControl('', [Validators.required]),
            [this.LAYER_TYPE_MARKERS_KEY]: new FormControl(experiment?.markers, [
                Validators.required,
            ]),
            [this.LAYER_TYPE_TRANSCRIPTS_KEY]: new FormControl(experiment?.transcripts, [
                Validators.required,
            ]),
            [this.LAYER_TYPE_MORPHOMETRICS_KEY]: new FormControl(experiment?.morphometrics, [
                Validators.required,
            ]),
        })
    }

    selectMarkers(markerName: string): void {
        if (this.selectedMarkers.includes(markerName)) {
            this.selectedMarkers = this.selectedMarkers.filter(marker => marker !== markerName)
        } else {
            this.selectedMarkers.push(markerName)
        }
    }

    onMarkerCheckboxChange(event: any) {
        if (event.target.checked) {
            this.sourceLayers$.pipe(take(1)).subscribe(markers => {
                markers.forEach(marker => {
                    if (marker.name) {
                        this.selectMarkers(marker.name)
                    }
                })
            })
        } else {
            this.selectedMarkers = []
        }
    }

    // goToWiki(): void {
    //     window.open(`${this.wikiUrl}home/Working_with_jobs#h-7-umap-and-t-sne`, '_blank')
    // }
}
